import React, { useState } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import SignatureCanvas from '../components/SignatureCanvas';
import '../global/styles.css';

//////////////////////////////////////////////
// TRUCK CHECKOUT CONFIGURATION
//////////////////////////////////////////////
const TRUCK_CHECKOUT_CONFIG = {
  locationId: 'truck-checkout',
  locationName: 'Truck Checkout',
  tableName: 'truck_checkout_checks',
  storageFolder: 'truck_checkout'
};

function TruckCheckOut({ userInfo, isAdmin = false, onLogout, onNavigate }) {
  const [formData, setFormData] = useState({
    ambulance: '',
    truck_refueled: '',
    interior_cleaned: '',
    exterior_cleaned: '',
    mechanical_concerns: '',
    mechanical_concerns_who: '',
    keys_location: '',
    keys_next_shift_crew: '',
    iv_pumps_count: '',
    pump_1_number: '',
    pump_2_number: '',
    pump_3_number: '',
    pump_4_number: '',
    crew_member_1_name: '',
    crew_member_1_signature: '',
    crew_member_2_name: '',
    crew_member_2_license: '',
    crew_member_2_pin: '',
    crew_member_2_signature: ''
  });

  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('error');
  const [showSignature, setShowSignature] = useState(false);
  const [currentSignatureField, setCurrentSignatureField] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Clear conditional fields when parent changes
      if (field === 'mechanical_concerns' && value === 'No') {
        updated.mechanical_concerns_who = '';
      }
      if (field === 'keys_location' && value === 'Lock box') {
        updated.keys_next_shift_crew = '';
      }
      // When pump count drops, clear any pump-number slots that are no longer visible
      if (field === 'iv_pumps_count') {
        const count = parseInt(value, 10);
        const visible = Number.isFinite(count) ? count : 0;
        for (let slot = visible + 1; slot <= 4; slot += 1) {
          updated[`pump_${slot}_number`] = '';
        }
      }

      return updated;
    });
  };

  const showAlertPopup = (message, type = 'error') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
  };

  const handleSignatureClick = (field) => {
    setCurrentSignatureField(field);
    setShowSignature(true);
  };

  const handleSignatureConfirm = (dataURL) => {
    setFormData(prev => ({ ...prev, [currentSignatureField]: dataURL }));
    setShowSignature(false);
    setCurrentSignatureField('');
  };

  const handleSignatureCancel = () => {
    setShowSignature(false);
    setCurrentSignatureField('');
  };

  // Upload signature (base64) to Supabase Storage - matching existing pattern
  const uploadSignature = async (base64Data, folder, fileName) => {
    const base64Response = await fetch(base64Data);
    const blob = await base64Response.blob();

    const { data, error } = await supabase.storage
      .from('inventory')
      .upload(`${folder}/signatures/${fileName}`, blob, {
        contentType: 'image/png'
      });

    if (error) throw error;

    // Stored as a storage path; displayed through a short-lived signed URL.
    return data.path;
  };

  const handleSubmit = async () => {
    // Validation
    const requiredFields = [
      { field: 'ambulance', label: 'Ambulance' },
      { field: 'truck_refueled', label: 'Was truck refueled' },
      { field: 'interior_cleaned', label: 'Truck interior cleaned' },
      { field: 'exterior_cleaned', label: 'Truck exterior cleaned' },
      { field: 'mechanical_concerns', label: 'Mechanical concerns' },
      { field: 'keys_location', label: 'Where were keys left' },
      { field: 'iv_pumps_count', label: '# of IV Pumps' }
    ];

    for (const { field, label } of requiredFields) {
      if (!formData[field]) {
        showAlertPopup(`${label} is required.`);
        return;
      }
    }

    // Conditional validation
    if (formData.mechanical_concerns === 'Yes' && !formData.mechanical_concerns_who) {
      showAlertPopup('Please specify who was notified about mechanical concerns.');
      return;
    }

    if (formData.keys_location === 'Next shift' && !formData.keys_next_shift_crew) {
      showAlertPopup('Please enter the crew member names for next shift.');
      return;
    }

    // Conditional: require one pump-number entry per pump on the truck
    const pumpCount = parseInt(formData.iv_pumps_count, 10) || 0;
    for (let slot = 1; slot <= pumpCount; slot += 1) {
      if (!formData[`pump_${slot}_number`]) {
        showAlertPopup(`Pump ${slot} Number is required.`);
        return;
      }
    }

    // Signature validation
    if (!formData.crew_member_1_name) {
      showAlertPopup('Crew member 1 name is required.');
      return;
    }
    if (!formData.crew_member_1_signature) {
      showAlertPopup('Crew member 1 signature is required.');
      return;
    }
    if (!formData.crew_member_2_name) {
      showAlertPopup('Crew member 2 name is required.');
      return;
    }
    if (!formData.crew_member_2_license) {
      showAlertPopup('Crew member 2 license number is required.');
      return;
    }
    if (!formData.crew_member_2_pin) {
      showAlertPopup('Crew member 2 PIN is required.');
      return;
    }
    if (!formData.crew_member_2_signature) {
      showAlertPopup('Crew member 2 signature is required.');
      return;
    }

    setLoading(true);

    try {
      // Upload signatures to Supabase Storage
      const timestamp = new Date().getTime();
      const crew1SigFileName = `crew1_${timestamp}.png`;
      const crew2SigFileName = `crew2_${timestamp}.png`;

      const crew1SignatureUrl = await uploadSignature(
        formData.crew_member_1_signature,
        TRUCK_CHECKOUT_CONFIG.storageFolder,
        crew1SigFileName
      );

      const crew2SignatureUrl = await uploadSignature(
        formData.crew_member_2_signature,
        TRUCK_CHECKOUT_CONFIG.storageFolder,
        crew2SigFileName
      );

      // Prepare submission data with URLs instead of base64
      const submissionData = {
        ambulance: formData.ambulance,
        truck_refueled: formData.truck_refueled,
        interior_cleaned: formData.interior_cleaned,
        exterior_cleaned: formData.exterior_cleaned,
        mechanical_concerns: formData.mechanical_concerns,
        mechanical_concerns_who: formData.mechanical_concerns_who,
        keys_location: formData.keys_location,
        keys_next_shift_crew: formData.keys_next_shift_crew,
        iv_pumps_count: formData.iv_pumps_count,
        pump_1_number: formData.pump_1_number,
        pump_2_number: formData.pump_2_number,
        pump_3_number: formData.pump_3_number,
        pump_4_number: formData.pump_4_number,
        crew_member_1_name: formData.crew_member_1_name,
        crew_member_1_signature_url: crew1SignatureUrl,
        crew_member_2_name: formData.crew_member_2_name,
        crew_member_2_license: formData.crew_member_2_license,
        // crew_member_2_pin is not stored (never verified; a DB trigger strips it too).
        crew_member_2_signature_url: crew2SignatureUrl,
        submitted_by_first_name: userInfo.firstName,
        submitted_by_last_name: userInfo.lastName,
        submitted_by_email: userInfo.email
      };

      const { error } = await supabase
        .from(TRUCK_CHECKOUT_CONFIG.tableName)
        .insert([submissionData]);

      if (error) throw error;

      showAlertPopup('End of shift checkout submitted successfully!', 'success');

      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          ambulance: '',
          truck_refueled: '',
          interior_cleaned: '',
          exterior_cleaned: '',
          mechanical_concerns: '',
          mechanical_concerns_who: '',
          keys_location: '',
          keys_next_shift_crew: '',
          iv_pumps_count: '',
          pump_1_number: '',
          pump_2_number: '',
          pump_3_number: '',
          pump_4_number: '',
          crew_member_1_name: '',
          crew_member_1_signature: '',
          crew_member_2_name: '',
          crew_member_2_license: '',
          crew_member_2_pin: '',
          crew_member_2_signature: ''
        });
        // Navigate back to home
        onNavigate('home');
      }, 1500);
    } catch (err) {
      console.error('Error submitting:', err);
      showAlertPopup(`Error: ${err.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '20px' }}>
      {/* Hamburger Menu */}
      <HamburgerMenu
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      {/* Main Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', paddingTop: '20px' }}>

        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '40px'
        }}>
          <h1 style={{
            color: '#0E3293',
            fontSize: '32px',
            fontWeight: '600',
            marginBottom: '10px'
          }}>
            End of Shift Truck Checkout
          </h1>
          <p style={{ color: '#666', fontSize: '16px' }}>
            {userInfo.firstName} {userInfo.lastName}
          </p>
        </div>

        {/* Form */}
        <div style={{
          background: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '15px',
          padding: '40px',
          marginBottom: '30px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>

          {/* Ambulance */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Ambulance <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              className="form-input"
              value={formData.ambulance}
              onChange={(e) => handleInputChange('ambulance', e.target.value)}
            >
              <option value="">-- Select one</option>
              <option value="Mayo 21">Mayo 21</option>
              <option value="Mayo 22">Mayo 22</option>
              <option value="Mayo 23">Mayo 23</option>
              <option value="Mayo 24">Mayo 24</option>
              <option value="Mayo 25">Mayo 25</option>
            </select>
          </div>

          {/* Q1: Was truck refueled */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Q1. Was truck refueled <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="truck_refueled"
                  value="Yes"
                  checked={formData.truck_refueled === 'Yes'}
                  onChange={(e) => handleInputChange('truck_refueled', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="truck_refueled"
                  value="No"
                  checked={formData.truck_refueled === 'No'}
                  onChange={(e) => handleInputChange('truck_refueled', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                No
              </label>
            </div>
          </div>

          {/* Q2: Truck interior cleaned */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Q2. Truck interior cleaned <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="interior_cleaned"
                  value="Yes"
                  checked={formData.interior_cleaned === 'Yes'}
                  onChange={(e) => handleInputChange('interior_cleaned', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="interior_cleaned"
                  value="No"
                  checked={formData.interior_cleaned === 'No'}
                  onChange={(e) => handleInputChange('interior_cleaned', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                No
              </label>
            </div>
          </div>

          {/* Q3: Truck exterior cleaned */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Q3. Truck exterior cleaned <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="exterior_cleaned"
                  value="Yes"
                  checked={formData.exterior_cleaned === 'Yes'}
                  onChange={(e) => handleInputChange('exterior_cleaned', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="exterior_cleaned"
                  value="No"
                  checked={formData.exterior_cleaned === 'No'}
                  onChange={(e) => handleInputChange('exterior_cleaned', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                No
              </label>
            </div>
          </div>

          {/* Q4: Mechanical concerns */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Q4. Mechanical concerns <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="mechanical_concerns"
                  value="Yes"
                  checked={formData.mechanical_concerns === 'Yes'}
                  onChange={(e) => handleInputChange('mechanical_concerns', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="mechanical_concerns"
                  value="No"
                  checked={formData.mechanical_concerns === 'No'}
                  onChange={(e) => handleInputChange('mechanical_concerns', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                No
              </label>
            </div>
            
            {formData.mechanical_concerns === 'Yes' && (
              <div style={{ marginTop: '15px' }}>
                <label className="form-label" style={{ fontSize: '14px' }}>
                  If yes, who was notified? <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.mechanical_concerns_who}
                  onChange={(e) => handleInputChange('mechanical_concerns_who', e.target.value)}
                  placeholder="Enter name of person notified"
                />
              </div>
            )}
          </div>

          {/* Q5: Where were keys left */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Q5. Where were keys left <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="keys_location"
                  value="Lock box"
                  checked={formData.keys_location === 'Lock box'}
                  onChange={(e) => handleInputChange('keys_location', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                Lock box
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="keys_location"
                  value="Next shift"
                  checked={formData.keys_location === 'Next shift'}
                  onChange={(e) => handleInputChange('keys_location', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                Next shift
              </label>
            </div>
            
            {formData.keys_location === 'Next shift' && (
              <div style={{ marginTop: '15px' }}>
                <label className="form-label" style={{ fontSize: '14px' }}>
                  If next shift, put the crew members name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.keys_next_shift_crew}
                  onChange={(e) => handleInputChange('keys_next_shift_crew', e.target.value)}
                  placeholder="Enter crew member names"
                />
              </div>
            )}
          </div>

          {/* Q6: IV Pumps */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Q6. # of IV Pumps <span style={{ color: 'red' }}>*</span>
            </label>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
              Total number of pumps remaining on the truck at end of shift.
            </p>
            <select
              className="form-input"
              value={formData.iv_pumps_count}
              onChange={(e) => handleInputChange('iv_pumps_count', e.target.value)}
            >
              <option value="">-- Select one</option>
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
            </select>
          </div>

          {/* Pump Numbers — only render a slot per pump on the truck */}
          {Array.from(
            { length: parseInt(formData.iv_pumps_count, 10) || 0 },
            (_, i) => i + 1
          ).map((slot) => {
            const field = `pump_${slot}_number`;
            return (
              <div key={field} style={{ marginBottom: '25px' }}>
                <label className="form-label">
                  Pump {slot} Number <span style={{ color: 'red' }}>*</span>
                </label>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                  Select the pump number (1-30) currently in this slot.
                </p>
                <select
                  className="form-input"
                  value={formData[field]}
                  onChange={(e) => handleInputChange(field, e.target.value)}
                >
                  <option value="">-- Select one</option>
                  {Array.from({ length: 30 }, (_, i) => i + 1).map((num) => (
                    <option key={num} value={String(num)}>{num}</option>
                  ))}
                  <option value="N/A">N/A</option>
                </select>
              </div>
            );
          })}

          {/* Crew Member Signatures Section */}
          <div style={{
            marginTop: '40px',
            padding: '20px',
            background: '#f8f9fa',
            borderRadius: '10px',
            border: '2px solid #0E3293'
          }}>
            <h3 style={{
              color: '#0E3293',
              fontSize: '20px',
              marginBottom: '10px'
            }}>
              Crew Member Sign-Off
            </h3>
            <p style={{
              fontSize: '14px',
              color: '#666',
              marginBottom: '25px',
              lineHeight: '1.6'
            }}>
              Require a 2 person sign off (same process as the narcotics). If handing off with next shift, one person from each crew will sign for the keys. If not handing off, both crew members from the truck will sign that keys were returned to lock box.
            </p>

            {/* Crew Member 1 */}
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h4 style={{ color: '#333', marginBottom: '15px' }}>Crew Member 1</h4>
              
              <div style={{ marginBottom: '15px' }}>
                <label className="form-label">
                  Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.crew_member_1_name}
                  onChange={(e) => handleInputChange('crew_member_1_name', e.target.value)}
                  placeholder="Enter crew member name"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label className="form-label">
                  Signature <span style={{ color: 'red' }}>*</span>
                </label>
                {formData.crew_member_1_signature ? (
                  <div>
                    <img
                      src={formData.crew_member_1_signature}
                      alt="Crew Member 1 Signature"
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        maxWidth: '100%',
                        height: 'auto',
                        marginBottom: '10px'
                      }}
                    />
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleSignatureClick('crew_member_1_signature')}
                      style={{ fontSize: '14px', padding: '8px 16px' }}
                    >
                      Change Signature
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={() => handleSignatureClick('crew_member_1_signature')}
                  >
                    Click to Sign
                  </button>
                )}
              </div>
            </div>

            {/* Crew Member 2 */}
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px'
            }}>
              <h4 style={{ color: '#333', marginBottom: '15px' }}>Crew Member 2</h4>
              
              <div style={{ marginBottom: '15px' }}>
                <label className="form-label">
                  Name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.crew_member_2_name}
                  onChange={(e) => handleInputChange('crew_member_2_name', e.target.value)}
                  placeholder="Enter crew member name"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label className="form-label">
                  License Number <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.crew_member_2_license}
                  onChange={(e) => handleInputChange('crew_member_2_license', e.target.value)}
                  placeholder="Enter license number"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label className="form-label">
                  PIN <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="password"
                  className="form-input"
                  value={formData.crew_member_2_pin}
                  onChange={(e) => handleInputChange('crew_member_2_pin', e.target.value)}
                  placeholder="Enter PIN"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label className="form-label">
                  Signature <span style={{ color: 'red' }}>*</span>
                </label>
                {formData.crew_member_2_signature ? (
                  <div>
                    <img
                      src={formData.crew_member_2_signature}
                      alt="Crew Member 2 Signature"
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        maxWidth: '100%',
                        height: 'auto',
                        marginBottom: '10px'
                      }}
                    />
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleSignatureClick('crew_member_2_signature')}
                      style={{ fontSize: '14px', padding: '8px 16px' }}
                    >
                      Change Signature
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={() => handleSignatureClick('crew_member_2_signature')}
                  >
                    Click to Sign
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            className="btn btn-primary btn-full"
            onClick={handleSubmit}
            disabled={loading}
            style={{ padding: '15px', fontSize: '18px', marginTop: '20px' }}
          >
            {loading ? 'Submitting...' : 'Submit End of Shift Checkout'}
          </button>
        </div>
      </div>

      {/* Signature Canvas Modal */}
      {showSignature && (
        <SignatureCanvas
          title={currentSignatureField === 'crew_member_1_signature' ? 'Crew Member 1 Signature' : 'Crew Member 2 Signature'}
          onConfirm={handleSignatureConfirm}
          onCancel={handleSignatureCancel}
        />
      )}

      {/* Alert Popup */}
      {showAlert && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              backgroundColor: alertType === 'success' ? '#e8f5e8' : '#fee',
              color: alertType === 'success' ? '#2e7d32' : '#c33'
            }}>
              {alertType === 'success' ? '✓' : '✕'}
            </div>
            <h3 style={{
              margin: '0 0 15px',
              fontSize: '20px',
              color: '#333'
            }}>
              {alertType === 'success' ? 'Success' : 'Error'}
            </h3>
            <p style={{
              margin: '0 0 25px',
              color: '#666',
              lineHeight: '1.5'
            }}>
              {alertMessage}
            </p>
            <button
              onClick={() => setShowAlert(false)}
              className="btn btn-primary"
              style={{ width: '100%' }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TruckCheckOut;
